import { fnv1a32RecordHash } from "../science/provenance.mjs";

export const sources = [
  {
    id: "spice-gf",
    title: "SPICE Geometry Finder",
    organization: "NASA NAIF",
    type: "official software standard",
    url: "https://naif.jpl.nasa.gov/pub/naif/toolkit_docs/C/req/gf.html",
    trust: "primary",
    supports: "Geometry-event windows, angular separation, occultation, distance constraints.",
  },
  {
    id: "horizons-api",
    title: "JPL Horizons API",
    organization: "NASA/JPL",
    type: "official API",
    url: "https://ssd-api.jpl.nasa.gov/doc/horizons.html",
    trust: "primary",
    supports: "Ephemeris vectors, observer tables, light-time validation snapshots.",
  },
  {
    id: "horizons-mercury-2024",
    title: "JPL Horizons Mercury Physical Data",
    organization: "NASA/JPL Horizons",
    type: "live API snapshot checked 2026-06-19",
    url: "https://ssd.jpl.nasa.gov/api/horizons.api?format=json&COMMAND=%27199%27&OBJ_DATA=%27YES%27&MAKE_EPHEM=%27NO%27",
    trust: "primary",
    supports:
      "Mercury sidereal orbital period and perihelion/mean/aphelion solar constants for tolerance checks.",
  },
  {
    id: "nist-codata",
    title: "2022 CODATA Fundamental Physical Constants",
    organization: "NIST",
    type: "official constants table",
    url: "https://physics.nist.gov/cuu/Constants/Table/allascii.txt",
    trust: "primary",
    supports:
      "Exact speed of light in vacuum and exact Stefan-Boltzmann constant used in kernel tests.",
  },
  {
    id: "sbdb-api",
    title: "JPL Small-Body Database API",
    organization: "NASA/JPL",
    type: "official API",
    url: "https://ssd-api.jpl.nasa.gov/doc/sbdb.html",
    trust: "primary",
    supports: "Asteroid orbit and physical property candidate screening.",
  },
  {
    id: "cad-api",
    title: "SBDB Close Approach Data API",
    organization: "NASA/JPL CNEOS",
    type: "official API",
    url: "https://ssd-api.jpl.nasa.gov/doc/cad.html",
    trust: "primary",
    supports: "Close approach frequency and velocity screening for feedstock candidates.",
  },
  {
    id: "orekit",
    title: "Orekit",
    organization: "CS Group / open source community",
    type: "open-source astrodynamics library",
    url: "https://www.orekit.org/",
    trust: "open-source",
    supports: "Future propagation, event detection, frames, time scales, and orbit analysis.",
  },
  {
    id: "gmat",
    title: "General Mission Analysis Tool",
    organization: "NASA",
    type: "official open-source mission analysis tool",
    url: "https://software.nasa.gov/software/GSC-17177-1",
    trust: "primary",
    supports: "Trajectory validation, transfers, optimization, and mission design checks.",
  },
  {
    id: "openmdao",
    title: "OpenMDAO",
    organization: "NASA",
    type: "open-source optimization framework",
    url: "https://openmdao.org/",
    trust: "primary",
    supports: "Future multidisciplinary optimization and sensitivity analysis.",
  },
  {
    id: "dymos",
    title: "Dymos",
    organization: "OpenMDAO project",
    type: "open-source optimal-control library",
    url: "https://openmdao.github.io/dymos/",
    trust: "open-source",
    supports: "Trajectory/logistics optimal control, multidisciplinary sensitivity, and Pareto studies.",
  },
  {
    id: "tudat",
    title: "Tudat Space / TudatPy",
    organization: "TU Delft Astrodynamics Toolbox community",
    type: "open-source astrodynamics toolkit",
    url: "https://docs.tudat.space/en/latest/",
    trust: "open-source",
    supports: "Cross-tool propagation, estimation, mission design, and trajectory optimization checks.",
  },
  {
    id: "iau-sofa",
    title: "Standards of Fundamental Astronomy",
    organization: "IAU SOFA",
    type: "official astronomy standards",
    url: "https://www.iausofa.org/",
    trust: "primary",
    supports: "Time scales, reference systems, and astronomical transformations.",
  },
  {
    id: "pds",
    title: "Planetary Data System",
    organization: "NASA",
    type: "official archive",
    url: "https://pds.nasa.gov/datasearch/data-search/",
    trust: "primary",
    supports: "Mission data snapshots for MESSENGER and future resource evidence.",
  },
  {
    id: "mpc",
    title: "Minor Planet Center Data",
    organization: "International Astronomical Union Minor Planet Center",
    type: "official small-body observation data",
    url: "https://minorplanetcenter.net/data",
    trust: "primary",
    supports: "Small-body observation and designation records that should bridge SBDB candidate screens to original observation context.",
  },
  {
    id: "esa-psa",
    title: "ESA Planetary Science Archive",
    organization: "European Space Agency",
    type: "official planetary mission archive",
    url: "https://archives.esac.esa.int/psa/",
    trust: "primary",
    supports: "European planetary mission data for independent resource, surface, and environment evidence.",
  },
  {
    id: "jaxa-darts",
    title: "DARTS Space Science Data Archive",
    organization: "ISAS/JAXA",
    type: "official space science archive",
    url: "https://darts.isas.jaxa.jp/",
    trust: "primary",
    supports: "Japanese astronomy, solar physics, planetary, and microgravity data products for cross-agency source coverage.",
  },
  {
    id: "nasa-mercury",
    title: "Mercury Facts",
    organization: "NASA Science",
    type: "official factsheet",
    url: "https://science.nasa.gov/mercury/facts/",
    trust: "primary",
    supports: "Mercury temperature, distance, orbit, and sunlight context.",
  },
  {
    id: "nasa-dtn",
    title: "Delay/Disruption Tolerant Networking",
    organization: "NASA SCaN",
    type: "official program page",
    url: "https://www.nasa.gov/communicating-with-missions/delay-disruption-tolerant-networking/",
    trust: "primary",
    supports: "Store-and-forward communication model for disrupted links.",
  },
  {
    id: "spacex-rideshare",
    title: "SpaceX Rideshare Program",
    organization: "SpaceX",
    type: "commercial launch pricing page",
    url: "https://www.spacex.com/rideshare/",
    trust: "commercial-primary",
    supports: "Published rideshare pricing and payload-class constraints for launch-cost source intake.",
  },
  {
    id: "nasa-clps",
    title: "Commercial Lunar Payload Services",
    organization: "NASA",
    type: "official commercial lunar delivery program page",
    url: "https://www.nasa.gov/commercial-lunar-payload-services/",
    trust: "primary",
    supports: "Commercial lunar delivery program scope, provider pool, task-order context, and contract envelope.",
  },
  {
    id: "nasa-cost-estimating-handbook",
    title: "NASA Cost Estimating Handbook",
    organization: "NASA",
    type: "official cost-estimating guidance",
    url: "https://www.nasa.gov/reference/nasa-cost-estimating-handbook/",
    trust: "primary",
    supports: "Cost-estimating methodology, uncertainty, risk treatment, and model documentation expectations.",
  },
  {
    id: "nasa-se-handbook",
    title: "NASA Systems Engineering Handbook",
    organization: "NASA",
    type: "official systems-engineering handbook",
    url: "https://www.nasa.gov/reference/systems-engineering-handbook/",
    trust: "primary",
    supports: "Technology maturity, technical assessment, requirements, verification, validation, and life-cycle review context.",
  },
  {
    id: "usaspending",
    title: "USAspending.gov",
    organization: "U.S. Treasury / federal agencies",
    type: "official federal award data",
    url: "https://www.usaspending.gov/",
    trust: "primary",
    supports: "Federal contract and assistance award cross-checks for NASA and aerospace program spending records.",
  },
  {
    id: "sec-edgar",
    title: "SEC EDGAR",
    organization: "U.S. Securities and Exchange Commission",
    type: "official public company filing database",
    url: "https://www.sec.gov/edgar",
    trust: "primary",
    supports: "Public-company financial filings for market, capex, opex, risk, and revenue-context review.",
  },
  {
    id: "ccsds",
    title: "Consultative Committee for Space Data Systems",
    organization: "CCSDS member agencies",
    type: "international space data and communications standards",
    url: "https://ccsds.org/",
    trust: "primary",
    supports: "Standards context for space links, data systems, mission operations, and cross-support assumptions.",
  },
  {
    id: "nasa-spdf",
    title: "NASA Space Physics Data Facility",
    organization: "NASA Goddard",
    type: "official heliophysics data archive",
    url: "https://spdf.gsfc.nasa.gov/",
    trust: "primary",
    supports: "Space physics archive, CDAWeb, SSCWeb, OMNIWeb, and CDF data for space-environment assumptions.",
  },
  {
    id: "nasa-ccmc",
    title: "Community Coordinated Modeling Center",
    organization: "NASA Goddard",
    type: "official space-weather modeling center",
    url: "https://ccmc.gsfc.nasa.gov/",
    trust: "primary",
    supports: "Community space-weather models, validation services, and run-on-request environment evidence.",
  },
  {
    id: "noaa-swpc",
    title: "NOAA Space Weather Prediction Center Products and Data",
    organization: "NOAA/NWS",
    type: "official space-weather products",
    url: "https://www.spaceweather.gov/products-and-data",
    trust: "primary",
    supports: "Forecasts, alerts, solar wind, proton flux, geomagnetic, and solar-radio environment context.",
  },
  {
    id: "nist-chemistry-webbook",
    title: "NIST Chemistry WebBook SRD 69",
    organization: "NIST",
    type: "standard reference chemistry database",
    url: "https://webbook.nist.gov/chemistry/",
    trust: "primary",
    supports: "Thermochemical, thermophysical, spectra, and ion energetics data for process modeling.",
  },
  {
    id: "materials-project",
    title: "Materials Project API",
    organization: "Lawrence Berkeley National Laboratory / Materials Project",
    type: "open materials database API",
    url: "https://docs.materialsproject.org/downloading-data/using-the-api",
    trust: "primary",
    supports: "Sourceable material property records for manufacturability, thermal, and feedstock-screening modules.",
  },
  {
    id: "cantera",
    title: "Cantera",
    organization: "Cantera project",
    type: "open-source chemistry and transport toolkit",
    url: "https://cantera.org/",
    trust: "open-source",
    supports: "Chemical kinetics, thermodynamics, transport, plasma, fuel-cell, and deposition process models.",
  },
  {
    id: "moose-framework",
    title: "MOOSE Framework",
    organization: "Idaho National Laboratory",
    type: "open-source multiphysics framework",
    url: "https://mooseframework.inl.gov/",
    trust: "primary",
    supports: "Thermal, solid mechanics, phase-field, fluid, and multiphysics simulation artifacts.",
  },
  {
    id: "openfoam",
    title: "OpenFOAM",
    organization: "OpenCFD / OpenFOAM community",
    type: "open-source CFD toolkit",
    url: "https://www.openfoam.com/",
    trust: "open-source",
    supports: "Fluid flow, heat transfer, reacting flow, turbulence, and process-equipment CFD artifacts.",
  },
];

const sourceProvenanceDefaults = {
  license: "Public-source review required before redistribution of raw payloads.",
  access: "Public web/API",
  capturedAt: "2026-06-19",
  hashAlgorithm: "fnv1a32-stable-json-record",
  refreshPolicy: "Snapshot-first; live refresh must preserve query, units, uncertainty, and record hash.",
};

const sourceProvenanceOverrides = {
  "spice-gf": {
    sourceType: "ephemeris-event-software",
    units: ["TDB/ET", "km", "degrees", "event windows"],
    queryPolicy: "Kernel set, target/observer/constraint, tolerance, and generated window hash required.",
    uncertaintyMode: "Kernel coverage plus geometric tolerance.",
  },
  "horizons-api": {
    sourceType: "ephemeris-api",
    units: ["TDB", "AU", "km", "minutes", "degrees"],
    queryPolicy: "Full Horizons query string and response timestamp required.",
    uncertaintyMode: "API table tolerance plus downstream numeric tolerance.",
  },
  "horizons-mercury-2024": {
    sourceType: "body-physical-data",
    units: ["days", "AU", "W/m^2"],
    queryPolicy: "Body-data query and retrieved physical constants required.",
    uncertaintyMode: "Source-body data plus formula tolerance.",
  },
  "nist-codata": {
    sourceType: "physical-constants",
    units: ["SI"],
    queryPolicy: "Constants release and exact constant identifiers required.",
    uncertaintyMode: "CODATA uncertainty or exact-SI status.",
  },
  "sbdb-api": {
    sourceType: "small-body-orbit-api",
    units: ["AU", "km/s", "H", "diameter", "orbit condition code"],
    queryPolicy: "Object query, orbit solution date, and physical-property provenance required.",
    uncertaintyMode: "Orbit condition code, diameter uncertainty, and physical-data caveats.",
  },
  "cad-api": {
    sourceType: "close-approach-api",
    units: ["AU", "km/s", "date"],
    queryPolicy: "Date range, distance threshold, sort order, and close-approach record hash required.",
    uncertaintyMode: "Nominal approach values with orbit-solution caveats.",
  },
  orekit: {
    sourceType: "astrodynamics-library",
    units: ["frames", "time scales", "state vectors", "events"],
    queryPolicy: "Tool version, data context, frames, force model, and checksums required.",
    uncertaintyMode: "Model configuration and independent comparison residuals.",
  },
  gmat: {
    sourceType: "mission-analysis-tool",
    units: ["km", "km/s", "seconds", "constraints"],
    queryPolicy: "GMAT script hash, version, force model, optimizer, and run summary required.",
    uncertaintyMode: "Constraint residuals and cross-tool comparison.",
  },
  openmdao: {
    sourceType: "optimization-framework",
    units: ["dimensionless", "scenario-specific"],
    queryPolicy: "Problem definition, design variables, constraints, objective, and seed required.",
    uncertaintyMode: "Sensitivity and optimizer-convergence metadata.",
  },
  dymos: {
    sourceType: "optimal-control-library",
    units: ["states", "controls", "constraints", "objective values"],
    queryPolicy: "Problem definition, transcription, optimizer, constraints, objective, and run seed required.",
    uncertaintyMode: "Convergence status, sensitivity, and collocation residuals.",
  },
  tudat: {
    sourceType: "astrodynamics-toolkit",
    units: ["state vectors", "epochs", "frames", "residuals"],
    queryPolicy: "Tool version, body settings, integrator, propagator, and output hashes required.",
    uncertaintyMode: "Integrator settings, model configuration, and cross-tool residual bounds.",
  },
  "iau-sofa": {
    sourceType: "astronomy-standards",
    units: ["time scales", "reference frames"],
    queryPolicy: "Routine/version reference and transformation chain required.",
    uncertaintyMode: "Standards conformance and transform precision.",
  },
  pds: {
    sourceType: "mission-data-archive",
    units: ["instrument-specific"],
    queryPolicy: "Mission, instrument, product id, bundle, and citation required.",
    uncertaintyMode: "Instrument/product calibration metadata.",
  },
  mpc: {
    sourceType: "small-body-observation-archive",
    units: ["designation", "observation time", "astrometry"],
    queryPolicy: "Object designation, observation batch, citation, and retrieval timestamp required.",
    uncertaintyMode: "Observation residuals and orbit-solution bridge to SBDB/CAD.",
  },
  "esa-psa": {
    sourceType: "planetary-mission-archive",
    units: ["mission/product-specific"],
    queryPolicy: "Mission, instrument, product id, citation, and license/use terms required.",
    uncertaintyMode: "Product calibration, processing level, and instrument caveats.",
  },
  "jaxa-darts": {
    sourceType: "space-science-archive",
    units: ["mission/product-specific"],
    queryPolicy: "Mission, dataset, product id, retrieval timestamp, and citation required.",
    uncertaintyMode: "Dataset calibration and archive release notes.",
  },
  "nasa-mercury": {
    sourceType: "official-factsheet",
    units: ["AU", "days", "K", "W/m^2 context"],
    queryPolicy: "Page URL, access date, and derived-calculation trace required.",
    uncertaintyMode: "Factsheet summary caveats; not a substitute for mission data.",
  },
  "nasa-dtn": {
    sourceType: "communications-program-reference",
    units: ["protocol concepts", "network behavior"],
    queryPolicy: "Program reference, architecture assumption, and scenario mapping required.",
    uncertaintyMode: "Conceptual architecture until mission-specific link data exists.",
  },
  "spacex-rideshare": {
    sourceType: "commercial-launch-pricing",
    units: ["USD", "kg", "orbit class", "payload envelope"],
    queryPolicy: "Capture provider page, access timestamp, price table text, destination/orbit class, and terms caveats before use.",
    uncertaintyMode: "Commercial list price caveats, mission-specific integration costs, schedule, destination, and contract terms.",
    license: "Commercial public web page; quote minimally and preserve citation rather than redistributing raw content.",
  },
  "nasa-clps": {
    sourceType: "commercial-lunar-delivery-program",
    units: ["USD", "payload count", "delivery count", "contract envelope"],
    queryPolicy: "Capture NASA program page, date, task-order references, provider, payload mass/context, and award amount where available.",
    uncertaintyMode: "Task-order scope, modifications, mission risk, provider performance, and payload-specific services.",
  },
  "nasa-cost-estimating-handbook": {
    sourceType: "cost-estimating-methodology",
    units: ["cost estimate classes", "risk", "schedule", "uncertainty"],
    queryPolicy: "Document handbook version, estimating method, normalization year, risk treatment, and applied model assumptions.",
    uncertaintyMode: "Method selection, analogy quality, learning curves, schedule risk, and normalization assumptions.",
  },
  "nasa-se-handbook": {
    sourceType: "systems-engineering-methodology",
    units: ["life-cycle phase", "maturity", "verification", "validation"],
    queryPolicy: "Document handbook version, review/maturity concept used, and mapping to scenario/process gates.",
    uncertaintyMode: "Program-specific tailoring and maturity-assessment subjectivity.",
  },
  usaspending: {
    sourceType: "federal-award-database",
    units: ["USD", "award id", "recipient", "obligation", "period of performance"],
    queryPolicy: "Preserve query filters, award id, recipient, agency, obligation amounts, date range, and retrieval hash.",
    uncertaintyMode: "Award modification timing, obligation versus outlay, scope classification, and recipient hierarchy.",
  },
  "sec-edgar": {
    sourceType: "public-company-filings",
    units: ["USD", "filing date", "fiscal period", "risk factors"],
    queryPolicy: "Preserve CIK/ticker, accession number, filing date, statement section, XBRL fields, and retrieval hash.",
    uncertaintyMode: "Company-specific accounting policy, segment allocation, forward-looking statements, and market-cycle caveats.",
  },
  ccsds: {
    sourceType: "space-data-standard",
    units: ["standard-specific"],
    queryPolicy: "Publication id, version, area, and applied assumption required.",
    uncertaintyMode: "Standards conformance caveats and mission-specific implementation deltas.",
  },
  "nasa-spdf": {
    sourceType: "heliophysics-data-archive",
    units: ["CDF", "mission-specific plasma/radiation fields"],
    queryPolicy: "Dataset id, variables, time range, cadence, and retrieval hash required.",
    uncertaintyMode: "Instrument calibration, cadence, and archive-version caveats.",
  },
  "nasa-ccmc": {
    sourceType: "space-weather-modeling-center",
    units: ["model-specific"],
    queryPolicy: "Model, run id, inputs, version, and output hash required.",
    uncertaintyMode: "Model validation state, run configuration, and uncertainty bounds.",
  },
  "noaa-swpc": {
    sourceType: "space-weather-products",
    units: ["Kp", "Ap", "F10.7", "particle flux", "alerts"],
    queryPolicy: "Product id, issue time, forecast horizon, and archive link required.",
    uncertaintyMode: "Forecast horizon and product-specific verification notes.",
  },
  "nist-chemistry-webbook": {
    sourceType: "chemistry-reference-database",
    units: ["thermochemical", "thermophysical", "spectral"],
    queryPolicy: "Species identifier, property table, units, citation, and access date required.",
    uncertaintyMode: "NIST data notes, property method, and cited evaluation caveats.",
  },
  "materials-project": {
    sourceType: "materials-property-api",
    units: ["material-specific"],
    queryPolicy: "mp-id, fields, API version/client, method metadata, and retrieval hash required.",
    uncertaintyMode: "DFT/method limitations, provenance, and contributed-data caveats.",
  },
  cantera: {
    sourceType: "chemistry-transport-toolkit",
    units: ["species", "reactions", "transport", "temperature", "pressure"],
    queryPolicy: "Mechanism file, tool version, inputs, outputs, and mechanism hash required.",
    uncertaintyMode: "Mechanism validity range and solver tolerance.",
  },
  "moose-framework": {
    sourceType: "multiphysics-framework",
    units: ["mesh", "material properties", "PDE residuals", "solver tolerances"],
    queryPolicy: "Input file, application/version, mesh hash, material data, and output hash required.",
    uncertaintyMode: "Discretization, mesh convergence, solver residuals, and material uncertainty.",
  },
  openfoam: {
    sourceType: "cfd-toolkit",
    units: ["mesh", "fields", "boundary conditions", "solver residuals"],
    queryPolicy: "Case directory hash, solver/version, mesh, boundary conditions, and result hash required.",
    uncertaintyMode: "Mesh convergence, turbulence model, chemistry model, and boundary-condition uncertainty.",
  },
};

export const sourceRegistryV2 = sources.map((source) => {
  const override = sourceProvenanceOverrides[source.id] ?? {};
  const recordBase = {
    sourceId: source.id,
    title: source.title,
    organization: source.organization,
    url: source.url,
    trust: source.trust,
    type: source.type,
    supports: source.supports,
    ...sourceProvenanceDefaults,
    ...override,
  };
  return {
    ...recordBase,
    recordHash: fnv1a32RecordHash(recordBase),
  };
});

export const assumptionRegistry = [
  {
    id: "assumption-snapshot-first",
    title: "Browser consumes reviewed snapshots first",
    state: "validated-product-rule",
    owner: "Science kernel",
    uncertainty: "Low",
    sourceIds: ["horizons-api", "sbdb-api", "cad-api", "spice-gf"],
    riskIfWrong: "Live API outages or schema changes could make the beta unreproducible.",
    testGate: "All displayed science claims must resolve to local source records or reviewed artifacts.",
  },
  {
    id: "assumption-no-mercury-first",
    title: "Mercury cannot be hardcoded as the winning scenario",
    state: "validated-product-rule",
    owner: "Scenario engine",
    uncertainty: "Low",
    sourceIds: ["horizons-api", "pds", "nasa-mercury"],
    riskIfWrong: "The product becomes advocacy rather than feasibility intelligence.",
    testGate: "Conservative weights must allow Moon-first or other scenarios to outrank Mercury.",
  },
  {
    id: "assumption-resource-composition-unknown",
    title: "Asteroid/Mercury resource composition remains unknown until directly sourced",
    state: "blocked-until-source-backed",
    owner: "Resource ledger",
    uncertainty: "High",
    sourceIds: ["pds", "sbdb-api", "cad-api"],
    riskIfWrong: "Candidate feedstock rankings could imply unavailable or unmeasured materials.",
    testGate: "Taxonomy, spectra, or mission data required before promotion.",
  },
  {
    id: "assumption-social-signals-market-only",
    title: "Forums and social platforms are market signals, not science evidence",
    state: "approved-governance-rule",
    owner: "Product strategy",
    uncertainty: "Low",
    sourceIds: [],
    riskIfWrong: "Unverified narratives could contaminate verified claim states.",
    testGate: "Social-source records cannot promote claims without a primary-source bridge.",
  },
  {
    id: "assumption-launch-cost-ranges",
    title: "Launch economics must use dated ranges and sensitivity bands",
    state: "hypothesis",
    owner: "Economics module",
    uncertainty: "High",
    sourceIds: [],
    riskIfWrong: "Scenario rankings could swing materially on stale or optimistic transport costs.",
    testGate: "Every economics run needs source date, mass class, destination, margin, and sensitivity.",
  },
];

export const modelArtifacts = [
  {
    id: "artifact-horizons-geometry-v1",
    name: "Horizons Earth/Mercury geometry snapshot",
    domain: "Ephemeris",
    claimState: "source-backed",
    modelType: "official-api-snapshot",
    sourceIds: ["horizons-api", "iau-sofa"],
    runtimeStatus: "deterministic-snapshot",
    query: "COMMAND='199', CENTER='500@399', EPHEM_TYPE='OBSERVER', TIME_TYPE='TDB'",
    units: ["TDB", "AU", "km", "minutes", "degrees"],
    uncertainty: "Declared table tolerance plus local formula tolerance.",
    validation: "Light-time and AU/km consistency tests pass.",
    readinessInputs: {
      sourceTraceability: 1,
      queryReproducibility: 0.9,
      unitsCoverage: 1,
      uncertaintyCoverage: 0.75,
      runtimeGenerated: 0.35,
      independentValidation: 0.45,
    },
    nextAction: "Generate a live refresh artifact and preserve response hash.",
  },
  {
    id: "artifact-sbdb-cad-screen-v1",
    name: "SBDB/CAD small-body candidate screen",
    domain: "Feedstock",
    claimState: "source-backed-blocked",
    modelType: "official-api-screen",
    sourceIds: ["sbdb-api", "cad-api"],
    runtimeStatus: "deterministic-snapshot",
    query: "CAD date/distance screen plus SBDB physical-property lookup.",
    units: ["AU", "km/s", "diameter confidence", "orbit condition code"],
    uncertainty: "Resource composition is unknown without taxonomy, spectra, or mission data.",
    validation: "Tests prevent close-approach geometry from implying composition.",
    readinessInputs: {
      sourceTraceability: 1,
      queryReproducibility: 0.75,
      unitsCoverage: 0.85,
      uncertaintyCoverage: 0.8,
      runtimeGenerated: 0.25,
      independentValidation: 0.25,
    },
    nextAction: "Attach candidate-specific taxonomy/spectra/PDS records before promotion.",
  },
  {
    id: "artifact-spice-gf-windows-v1",
    name: "SPICE Geometry Finder event-window contract",
    domain: "Event windows",
    claimState: "adapter-contract",
    modelType: "geometry-event-window",
    sourceIds: ["spice-gf"],
    runtimeStatus: "adapter-fixture",
    query: "Kernel set plus angular-separation/occultation constraints.",
    units: ["TDB/ET", "event windows", "degrees", "km"],
    uncertainty: "Kernel coverage and GF constraint tolerance required.",
    validation: "Boundary contract exists; runtime kernel-hash artifact not generated yet.",
    readinessInputs: {
      sourceTraceability: 0.85,
      queryReproducibility: 0.65,
      unitsCoverage: 0.8,
      uncertaintyCoverage: 0.65,
      runtimeGenerated: 0,
      independentValidation: 0.2,
    },
    nextAction: "Run SPICE with recorded kernels and store event-window hash.",
  },
  {
    id: "artifact-trajectory-sidecar-v1",
    name: "Orekit/GMAT/Tudat trajectory validation sidecar",
    domain: "Trajectory",
    claimState: "blocked",
    modelType: "cross-tool-trajectory-validation",
    sourceIds: ["orekit", "gmat", "tudat", "openmdao", "dymos"],
    runtimeStatus: "contract-ready-no-runtime-artifact",
    query: "Scenario epoch, force model, transfer constraints, optimizer settings.",
    units: ["km", "km/s", "seconds", "constraint residuals"],
    uncertainty: "Requires independent propagation residuals and model-configuration trace.",
    validation: "Artifact schema exists; no runtime comparison generated yet.",
    readinessInputs: {
      sourceTraceability: 0.65,
      queryReproducibility: 0.55,
      unitsCoverage: 0.75,
      uncertaintyCoverage: 0.45,
      runtimeGenerated: 0,
      independentValidation: 0,
    },
    nextAction: "Create offline sidecar job and compare at least one transfer window.",
  },
  {
    id: "artifact-space-environment-v1",
    name: "Space environment and radiation evidence contract",
    domain: "Space environment",
    claimState: "hypothesis",
    modelType: "space-weather-environment-screen",
    sourceIds: ["nasa-spdf", "nasa-ccmc", "noaa-swpc"],
    runtimeStatus: "design-contract",
    query: "Heliophysics datasets, space-weather model runs, and forecast/archive products.",
    units: ["particle flux", "Kp/Ap", "F10.7", "radiation/environment indices"],
    uncertainty: "High until mission/site-specific time ranges, shielding assumptions, and model validation records are attached.",
    validation: "No space-environment artifact is generated yet.",
    readinessInputs: {
      sourceTraceability: 0.55,
      queryReproducibility: 0.35,
      unitsCoverage: 0.45,
      uncertaintyCoverage: 0.35,
      runtimeGenerated: 0,
      independentValidation: 0,
    },
    nextAction: "Add SPDF/CCMC/SWPC adapters for site-specific radiation and solar-weather assumptions.",
  },
  {
    id: "artifact-materials-process-v1",
    name: "Materials/process physics module contract",
    domain: "Manufacturing",
    claimState: "hypothesis",
    modelType: "thermal-material-process-fit",
    sourceIds: [
      "nist-codata",
      "nist-chemistry-webbook",
      "materials-project",
      "cantera",
      "moose-framework",
      "openfoam",
      "pds",
      "openmdao",
    ],
    runtimeStatus: "design-contract",
    query: "Material property source, thermal environment, process window, energy balance.",
    units: ["K", "W/m^2", "kg", "kWh", "stress/temperature where sourced"],
    uncertainty: "High until NIST/materials/process data adapters are attached.",
    validation: "No physics-grade material model is generated yet.",
    readinessInputs: {
      sourceTraceability: 0.5,
      queryReproducibility: 0.35,
      unitsCoverage: 0.55,
      uncertaintyCoverage: 0.45,
      runtimeGenerated: 0,
      independentValidation: 0,
    },
    nextAction: "Add NIST/Materials Project/Cantera/MOOSE/OpenFOAM artifact adapters.",
  },
  {
    id: "artifact-economics-uncertainty-v1",
    name: "Economics and Monte Carlo frontier artifact",
    domain: "Business model",
    claimState: "model-derived-unvalidated",
    modelType: "portfolio-sensitivity-model",
    sourceIds: [
      "spacex-rideshare",
      "nasa-clps",
      "nasa-cost-estimating-handbook",
      "nasa-se-handbook",
      "usaspending",
      "sec-edgar",
    ],
    runtimeStatus: "deterministic-generated-monte-carlo",
    query: "Seeded planning ranges for launch price, capital, mass-to-site, horizon, and demand-readiness proxy.",
    units: ["USD", "kg", "tonnes", "years", "probability"],
    uncertainty: "High until dated cost ranges and source-backed market assumptions are attached.",
    validation: "Generated economics-frontier artifact records seeded samples, scenario rank stability, funding coverage, and blockers.",
    readinessInputs: {
      sourceTraceability: 0.2,
      queryReproducibility: 0.8,
      unitsCoverage: 0.8,
      uncertaintyCoverage: 0.75,
      runtimeGenerated: 0.65,
      independentValidation: 0,
    },
    nextAction: "Attach dated launch-cost, capex, opex, and market-source records before promotion.",
  },
].map((artifact) => {
  const recordBase = {
    id: artifact.id,
    name: artifact.name,
    domain: artifact.domain,
    claimState: artifact.claimState,
    sourceIds: artifact.sourceIds,
    runtimeStatus: artifact.runtimeStatus,
    query: artifact.query,
    units: artifact.units,
    uncertainty: artifact.uncertainty,
    validation: artifact.validation,
  };
  return {
    ...artifact,
    artifactHash: fnv1a32RecordHash(recordBase),
  };
});

export const scenarioNotebooks = [
  {
    id: "notebook-moon-first",
    title: "Moon-first industrial bootstrap",
    audience: "near-term feasibility reviewer",
    sourceIds: ["pds", "nasa-dtn"],
    artifactIds: ["artifact-economics-uncertainty-v1", "artifact-materials-process-v1"],
    openQuestions: ["Polar volatile access", "power storage", "surface logistics", "customer demand"],
  },
  {
    id: "notebook-mercury-orbit",
    title: "Mercury-orbit energy and thermal trade",
    audience: "high-energy manufacturing reviewer",
    sourceIds: ["horizons-api", "spice-gf", "nasa-mercury"],
    artifactIds: ["artifact-horizons-geometry-v1", "artifact-spice-gf-windows-v1"],
    openQuestions: ["Thermal rejection", "solar interference windows", "transport burden", "resource proof"],
  },
  {
    id: "notebook-asteroid-feedstock",
    title: "NEA feedstock screening",
    audience: "resource and logistics reviewer",
    sourceIds: ["sbdb-api", "cad-api", "pds"],
    artifactIds: ["artifact-sbdb-cad-screen-v1", "artifact-trajectory-sidecar-v1"],
    openQuestions: ["Composition evidence", "low-vinf cadence", "processing site", "returned material value"],
  },
];

export const formulas = [
  {
    id: "solar-flux",
    name: "Solar flux factor and irradiance",
    class: "Established physics",
    expression: "F = 1 / r_au^2; S = 1361 * F W/m^2",
    units: "AU, W/m^2",
    confidence: "High",
    sourceIds: ["horizons-mercury-2024", "nasa-mercury"],
    sampleInputs: { rAu: 0.3871 },
  },
  {
    id: "thermal-equilibrium",
    name: "Radiative equilibrium temperature",
    class: "Established physics with simplified assumptions",
    expression: "T = ((1 - A)S / (epsilon*sigma))^(1/4); spherical model divides S by 4",
    units: "K",
    confidence: "Medium",
    sourceIds: ["nist-codata", "horizons-mercury-2024", "nasa-mercury"],
    sampleInputs: { irradianceWm2: 9075, albedo: 0.12, emissivity: 0.9 },
  },
  {
    id: "light-time",
    name: "One-way and round-trip light time",
    class: "Established physics",
    expression: "t = distance_km / 299792.458",
    units: "seconds, minutes",
    confidence: "High",
    sourceIds: ["nist-codata", "horizons-api", "spice-gf"],
    sampleInputs: { distanceKm: 120_000_000 },
  },
  {
    id: "synodic-period",
    name: "Earth-Mercury synodic period",
    class: "Established orbital relation",
    expression: "P_syn = 1 / |1/P_mercury - 1/P_earth|",
    units: "days",
    confidence: "High",
    sourceIds: ["horizons-mercury-2024", "horizons-api", "iau-sofa"],
    sampleInputs: { periodA: 87.9691, periodB: 365.25636 },
  },
  {
    id: "free-space-path-loss",
    name: "Free-space path loss",
    class: "Established radio link approximation",
    expression: "FSPL = 20log10((4*pi*d_m*f_hz)/c)",
    units: "dB",
    confidence: "Medium",
    sourceIds: ["nist-codata", "nasa-dtn"],
    sampleInputs: { distanceKm: 120_000_000, frequencyGHz: 8.4 },
  },
  {
    id: "resource-confidence",
    name: "Resource Confidence Score",
    class: "Planner heuristic",
    expression: "weighted source quality, measurement directness, recency, confirmation, and uncertainty",
    units: "0-100",
    confidence: "Experimental",
    sourceIds: ["pds"],
    sampleInputs: {
      sourceQuality: 0.95,
      measurementDirectness: 0.85,
      recency: 0.65,
      independentConfirmation: 0.8,
      uncertaintyPenalty: 0.25,
    },
  },
  {
    id: "process-body-fit",
    name: "Process-Body Fit Score",
    class: "Planner heuristic",
    expression: "vacuum + energy + feedstock - thermal - tooling burden",
    units: "0-100",
    confidence: "Experimental",
    sourceIds: ["spice-gf", "pds"],
    sampleInputs: {
      vacuumAdvantage: 0.9,
      energyAvailability: 0.95,
      thermalPenalty: 0.75,
      toolingBurden: 0.65,
      feedstockMatch: 0.45,
    },
  },
  {
    id: "autonomy-load",
    name: "Autonomy Load Index",
    class: "Planner heuristic",
    expression: "blackout duration + process duration + criticality - local recovery capability",
    units: "0-100",
    confidence: "Experimental",
    sourceIds: ["nasa-dtn", "spice-gf"],
    sampleInputs: {
      blackoutDays: 8,
      processDurationDays: 4,
      commandCriticality: 0.7,
      localFaultRecovery: 0.45,
    },
  },
  {
    id: "source-quality",
    name: "Source Quality Score",
    class: "Planner heuristic",
    expression: "authority + reproducibility + license clarity + uncertainty + freshness + independence",
    units: "0-100",
    confidence: "Experimental",
    sourceIds: ["pds", "horizons-api", "nist-codata"],
    sampleInputs: {
      authority: 0.95,
      reproducibility: 0.9,
      licenseClarity: 0.8,
      uncertaintyDisclosure: 0.75,
      freshness: 0.7,
      independence: 0.6,
    },
  },
  {
    id: "model-artifact-readiness",
    name: "Model Artifact Readiness Score",
    class: "Planner heuristic",
    expression: "source traceability + query + units + uncertainty + runtime artifact + independent validation",
    units: "0-100",
    confidence: "Experimental",
    sourceIds: ["spice-gf", "orekit", "gmat", "openmdao"],
    sampleInputs: {
      sourceTraceability: 0.85,
      queryReproducibility: 0.75,
      unitsCoverage: 0.8,
      uncertaintyCoverage: 0.7,
      runtimeGenerated: 0.25,
      independentValidation: 0.2,
    },
  },
];

export const claims = [
  {
    id: "claim-mercury-flux",
    claim: "Mercury solar flux ranges roughly from 4.6x to 10.6x Earth, with mean near 6.7x.",
    domain: "Energy",
    status: "Verified",
    confidence: 0.92,
    sourceIds: ["nasa-mercury"],
    impact: "High",
    lastVerified: "2026-06-18",
    notes: "Calculated by inverse square from Mercury's orbital distance range.",
  },
  {
    id: "claim-de440-boundary",
    claim: "DE440 should be treated as an operational ephemeris window, not a 50,000-year calendar.",
    domain: "Ephemeris",
    status: "Verified",
    confidence: 0.9,
    sourceIds: ["horizons-api", "spice-gf"],
    impact: "High",
    lastVerified: "2026-06-18",
    notes: "The app labels kernel coverage and avoids long-range precision claims.",
  },
  {
    id: "claim-helium3",
    claim: "Mercury helium-3 is a possible solar-wind implantation hypothesis, not a verified resource.",
    domain: "Resources",
    status: "Hypothesis",
    confidence: 0.25,
    sourceIds: ["pds"],
    impact: "Medium",
    lastVerified: "2026-06-18",
    notes: "Requires concentration, extraction, and throughput evidence before promotion.",
  },
  {
    id: "claim-spf-separation",
    claim: "Vacuum anisotropic sheet forming and SPF/DB must be modeled as separate process cards.",
    domain: "Manufacturing",
    status: "Likely",
    confidence: 0.78,
    sourceIds: ["pds"],
    impact: "High",
    lastVerified: "2026-06-18",
    notes: "Avoids conflating cold/low-temperature anisotropy preservation with elevated-temperature SPF/DB.",
  },
  {
    id: "claim-dtn",
    claim: "Disrupted interplanetary communications should use a store-and-forward planning model.",
    domain: "Comms",
    status: "Verified",
    confidence: 0.88,
    sourceIds: ["nasa-dtn"],
    impact: "High",
    lastVerified: "2026-06-18",
    notes: "This supports contact-plan pages and autonomy-load scoring.",
  },
  {
    id: "claim-mercury-core",
    claim: "Mercury's iron core is strategically interesting but not an early accessible surface resource.",
    domain: "Resources",
    status: "Likely",
    confidence: 0.7,
    sourceIds: ["nasa-mercury", "pds"],
    impact: "High",
    lastVerified: "2026-06-18",
    notes: "Early body ledgers must distinguish surface/shallow resources from deep interior composition.",
  },
];

export const readinessCategories = [
  {
    id: "local-functional-beta",
    label: "Local functional beta",
    baselineScore: 70,
    currentScore: 98,
    targetScore: 98,
    owner: "Product engineering",
    currentBlockers: [
      "Desktop and mobile browser regression now covers all eleven primary pages plus formula recomputation, snapshot/report/claims/package JSON exports, and approval-log mutation.",
      "Shared UI primitives, the app shell/navigation/status strip, all primary page modules, Scenario Compare evidence inspector panels, Source Library evidence panels, Research Packages evidence panels, Solar Clock orbit/timeline chart primitives, Process Lab flow/body-fit chart primitives, shared evidence panels across Research Packages, Approval Board, Scenario Compare, Source Library, Asteroid Scanner, Body Ledger, Process Lab, Formula Lab, Solar Clock, and Platform Roadmap detail sections, shared table controls, shared evidence stack lists across Source Library and Research Packages queues, proof-gap sections, blocker summaries, and customer package cards, shared score-bar chart primitives across Command Overview, Scenario Compare, Asteroid Scanner, and Source Library, app-level JSON export payloads, app-level export controls, hash-backed review entries, shared review action controls, and the Claims Register JSON export control are split out; local functional beta target is met pending the separate UI approval, science-runtime, CI promotion, Access owner-review, and alert-recovery gates.",
    ],
    fixes: [
      "Keep Solar Clock and Process Lab visual chart primitives covered by modularization tests.",
      "Keep browser checks for snapshot/report/claims export actions and approval-log mutation green.",
      "Keep committed desktop and mobile rendered-browser smoke tests green for every primary page.",
    ],
  },
  {
    id: "science-math-credibility",
    label: "Science/math credibility for beta",
    baselineScore: 55,
    currentScore: 96,
    targetScore: 98,
    owner: "Science kernel",
    currentBlockers: [
      "Source Registry 2.0, assumption registry, scenario notebooks, generated model-artifact bundles, seeded scenario uncertainty analysis, generated economics frontier artifacts, business-case planning heuristics, one SPICE GF runtime artifact, and one OpenMDAO/Dymos beta optimization artifact are now local and tested, but Orekit/GMAT/Tudat runtime artifacts are not yet generated.",
      "Asteroid candidate CAD and SBDB source payloads are captured for the current worklist, but promotion remains blocked until MPC bridging, source-use review, human source-custody approval, and taxonomy/spectra/mission-archive composition evidence replace draft reviewer records.",
    ],
    fixes: [
      "Keep established formulas covered by unit tests and source-integrity tests.",
      "Compare nominal solar irradiance against a cached Horizons Mercury snapshot with explicit tolerance.",
      "Label every planner score as heuristic and prevent heuristic claims from becoming verified physics.",
      "Preserve source query, units, uncertainty, license, and record hash metadata for every promoted model output.",
      "Regenerate deterministic model-artifact bundles before release and block promotion until runtime evidence passes.",
      "Keep seeded uncertainty ranges visible beside deterministic rankings so single-point scenario winners are not overstated.",
      "Keep capital, launch-mass, and horizon assumptions visible as planning heuristics until dated cost-source records are attached.",
      "Run live Horizons/SBDB/CAD refresh sampling and preserve generated source-query artifacts before claiming operational ephemerides.",
    ],
  },
  {
    id: "ui-review-readiness",
    label: "UI review readiness",
    baselineScore: 60,
    currentScore: 90,
    targetScore: 98,
    owner: "Product design",
    currentBlockers: [
      "The product shell and six primary pages have been rebuilt closer to the accepted mockup direction, but side-by-side screenshot fidelity still needs final page-by-page approval.",
      "Mobile no-overflow rendering and collapsible navigation are covered, but final typography/spacing approval against the reference set is still pending.",
    ],
    fixes: [
      "Finish propagating the code-native SVG logo/wordmark into export payloads and final review artifacts.",
      "Rebuild Command Overview, Scenario Compare, Solar Clock, Body Ledger, Process Lab, and Claims Register from the mockups.",
      "Run a mismatch ledger against the accepted mockups before approval.",
    ],
  },
  {
    id: "deployment-readiness",
    label: "Deployment readiness",
    baselineScore: 35,
    currentScore: 96,
    targetScore: 98,
    owner: "Release engineering",
    currentBlockers: [
      "Cloudflare Pages and the custom hostname are live from a git baseline, and a CI/CD workflow is committed, but the repo still needs a remote/secrets-backed run before it counts as reproducible promotion.",
      "Cloudflare Access anonymous challenge and monitor service-token verification pass; owner browser review remains a human approval step.",
    ],
    fixes: [
      "Keep `npm run verify` green and deploy the Vite `dist` artifact only after visual approval.",
      "Use `npm run deploy:pages` for repeatable Pages deployment.",
      "Run CI/CD deployment from a named release branch or protected main baseline.",
      "Verify Cloudflare Access with an owner browser session and record the approval evidence.",
    ],
  },
  {
    id: "public-gated-live",
    label: "Public/gated website live status",
    baselineScore: 0,
    currentScore: 96,
    targetScore: 98,
    owner: "Cloudflare/DNS",
    currentBlockers: [
      "The custom hostname is protected by Cloudflare Access; anonymous requests receive an Access challenge and the monitor service token reaches the app.",
      "A Worker Cron monitor and KV state store are live and healthy, but alert webhook/recovery proof still requires a real webhook or email relay.",
    ],
    fixes: [
      "Record owner/reviewer browser approval after Cloudflare Access login.",
      "Verify alert and recovery delivery once a real webhook or email relay is attached.",
    ],
  },
];

export const bodies = [
  {
    id: "mercury",
    name: "Mercury",
    summary: "Energy-rich, vacuum-favorable, thermally brutal, and resource-uncertain.",
    metrics: [
      ["Solar flux", "4.6x-10.6x Earth"],
      ["Mean orbital radius", "0.387 AU"],
      ["Comms risk", "High"],
      ["Thermal risk", "Extreme"],
    ],
    resources: [
      ["Solar energy", "Verified", "High-value process heat and power."],
      ["Vacuum/exosphere", "Likely", "Strong fit for contamination-sensitive manufacturing."],
      ["Polar water ice", "Likely", "Model as high-value but geographically constrained."],
      ["Mg-rich silicates / sulfur chemistry", "Likely", "Resource ledger candidate, needs processed map."],
      ["Surface iron abundance", "Low", "Do not assume core metals are accessible."],
      ["Helium-3", "Hypothesis", "Weak until concentration and extraction evidence exists."],
    ],
  },
  {
    id: "moon",
    name: "Moon",
    summary: "Closest bootstrap yard with strong operational learning value.",
    metrics: [
      ["Solar flux", "1x Earth"],
      ["Transport burden", "Low relative"],
      ["Comms risk", "Low"],
      ["Thermal risk", "High but familiar"],
    ],
    resources: [
      ["Regolith oxygen", "Likely", "Mature ISRU research lane."],
      ["Polar volatiles", "Likely", "High-value bootstrap resource."],
      ["Proximity", "Verified", "Best testing and logistics surface."],
    ],
  },
  {
    id: "asteroids",
    name: "Asteroids",
    summary: "Potential feedstock bank with high uncertainty and high optionality.",
    metrics: [
      ["Candidate count", "Large"],
      ["Composition", "Object-specific"],
      ["Transport", "Highly variable"],
      ["Evidence risk", "High"],
    ],
    resources: [
      ["Metals", "Hypothesis", "Requires taxonomy/spectra/mission-specific support."],
      ["Volatiles", "Hypothesis", "Object-specific, never assume."],
      ["Orbital accessibility", "Likely", "Screen with SBDB and close-approach data."],
    ],
  },
  {
    id: "mars",
    name: "Mars",
    summary: "Major exploration target, but weaker for first industrial-capacity maximization.",
    metrics: [
      ["Solar flux", "0.43x Earth"],
      ["Atmosphere", "Thin CO2"],
      ["Comms risk", "Periodic conjunction"],
      ["Thermal risk", "Moderate/high"],
    ],
    resources: [
      ["CO2 atmosphere", "Verified", "Useful for oxygen demos and propellant chains."],
      ["Water ice", "Likely", "Geographically constrained."],
      ["Industrial energy", "Risk", "Weaker solar than inner system."],
    ],
  },
  {
    id: "venus",
    name: "Venus / Venus Orbit",
    summary: "Relay and solar-orbit infrastructure candidate, not a first surface factory.",
    metrics: [
      ["Solar flux", "1.9x Earth"],
      ["Relay value", "Medium"],
      ["Surface risk", "Extreme"],
      ["Orbital value", "Medium"],
    ],
    resources: [
      ["Relay geometry", "Hypothesis", "Evaluate with contact windows."],
      ["Solar energy", "Verified", "Useful in orbit, not surface-first."],
    ],
  },
];

export const processes = [
  {
    id: "vacuum-sheet",
    name: "Vacuum Anisotropic Sheet Manufacturing",
    maturity: "Experimental planner concept",
    uncertainty: "High",
    bestFit: "Mercury orbit, asteroid stations, cislunar vacuum testbed",
    flow: ["rolled sheet", "vacuum handling", "controlled forming", "NDE", "structural panel"],
    parameters: [
      ["Temperature regime", "Low/warm controlled; below recrystallization target"],
      ["Core value", "Preserve directional worked/rolled structure where possible"],
      ["Main risk", "Tooling, reaction forces, anchoring, NDE at scale"],
    ],
  },
  {
    id: "spf-db",
    name: "Superplastic Forming / Diffusion Bonding",
    maturity: "Established aerospace family; scale uncertain",
    uncertainty: "Medium-high",
    bestFit: "Cislunar testbed, controlled orbital facilities",
    flow: ["fine-grained alloy", "elevated temperature", "forming", "bonding", "integral structure"],
    parameters: [
      ["Temperature regime", "Elevated; process-specific"],
      ["Core value", "Complex near-net aerospace structures"],
      ["Main risk", "Large die mass and thermal distortion"],
    ],
  },
  {
    id: "oxygen-isru",
    name: "Oxygen Extraction / ISRU",
    maturity: "Active research and demo lane",
    uncertainty: "Medium",
    bestFit: "Moon, Mars, selected asteroids",
    flow: ["regolith/CO2/water", "processing", "separation", "storage", "propellant/life support"],
    parameters: [
      ["Core value", "Reduces Earth-launched mass"],
      ["Main risk", "Throughput and reliability at industrial scale"],
    ],
  },
];

export const scenarios = [
  {
    id: "hybrid",
    name: "Hybrid Moon -> Asteroid -> Mercury",
    summary: "Bootstrap close, screen feedstock, then scale toward Mercury energy advantage.",
    benefits: { energy: 82, vacuum: 78, optionality: 92, processFit: 82, resourceConfidence: 68 },
    risks: { transport: 46, thermal: 45, comms: 42, feedstock: 55, processMaturity: 46 },
    uncertaintyPenalty: 12,
  },
  {
    id: "moon-first",
    name: "Moon-first Bootstrap",
    summary: "Fastest learning loop and lowest communications burden.",
    benefits: { energy: 50, vacuum: 64, optionality: 76, processFit: 70, resourceConfidence: 74 },
    risks: { transport: 25, thermal: 48, comms: 15, feedstock: 42, processMaturity: 38 },
    uncertaintyPenalty: 8,
  },
  {
    id: "asteroid-first",
    name: "Asteroid Feedstock-first",
    summary: "Potential materials bank, but composition and operations are object-specific.",
    benefits: { energy: 61, vacuum: 85, optionality: 84, processFit: 75, resourceConfidence: 45 },
    risks: { transport: 58, thermal: 38, comms: 48, feedstock: 72, processMaturity: 55 },
    uncertaintyPenalty: 18,
  },
  {
    id: "mercury-orbit",
    name: "Mercury Orbit-first",
    summary: "Maximum long-term energy/vacuum upside, high logistics and autonomy burden.",
    benefits: { energy: 97, vacuum: 92, optionality: 86, processFit: 84, resourceConfidence: 48 },
    risks: { transport: 86, thermal: 82, comms: 78, feedstock: 70, processMaturity: 62 },
    uncertaintyPenalty: 24,
  },
  {
    id: "mercury-polar",
    name: "Mercury Polar Surface-first",
    summary: "Polar volatile access is attractive, but surface thermal/comms risk is severe.",
    benefits: { energy: 88, vacuum: 86, optionality: 80, processFit: 74, resourceConfidence: 56 },
    risks: { transport: 90, thermal: 88, comms: 82, feedstock: 64, processMaturity: 66 },
    uncertaintyPenalty: 26,
  },
  {
    id: "mars-first",
    name: "Mars-first Conventional Expansion",
    summary: "Strong exploration ecosystem, weak for first industrial-capacity maximization.",
    benefits: { energy: 36, vacuum: 22, optionality: 65, processFit: 46, resourceConfidence: 65 },
    risks: { transport: 62, thermal: 56, comms: 64, feedstock: 48, processMaturity: 44 },
    uncertaintyPenalty: 12,
  },
];

export const asteroidCandidates = [
  {
    id: "candidate-a",
    name: "NEA screen A",
    status: "Synthetic workflow record",
    sourceIds: [],
    promotionBlocked: true,
    note: "Synthetic reviewer snapshot; replace with SBDB query record before promotion.",
    inputs: {
      closeApproachFrequency: 0.8,
      vInfKms: 5.2,
      diameterConfidence: 0.65,
      orbitAccessibility: 0.76,
      sourceUncertainty: 0.42,
      resourceClassConfidence: 0.35,
    },
  },
  {
    id: "candidate-b",
    name: "NEA screen B",
    status: "Synthetic workflow record",
    sourceIds: [],
    promotionBlocked: true,
    note: "Higher velocity demo record; replace with CAD close-approach data before promotion.",
    inputs: {
      closeApproachFrequency: 0.55,
      vInfKms: 8.1,
      diameterConfidence: 0.72,
      orbitAccessibility: 0.5,
      sourceUncertainty: 0.48,
      resourceClassConfidence: 0.38,
    },
  },
  {
    id: "candidate-c",
    name: "NEA screen C",
    status: "Synthetic workflow record",
    sourceIds: [],
    promotionBlocked: true,
    note: "Promising geometry demo record; resource class remains unknown until sourced.",
    inputs: {
      closeApproachFrequency: 0.7,
      vInfKms: 4.7,
      diameterConfidence: 0.55,
      orbitAccessibility: 0.82,
      sourceUncertainty: 0.5,
      resourceClassConfidence: 0.28,
    },
  },
];
